/* eslint-disable no-unused-vars */
import React, { forwardRef, useState, useRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { ErrorMarker, Encrypt, ArrowDropDown } from 'assets/icons';

import * as S from './CustomInput.styled';

function validateNumberValue(input) {
  const regex = /^$|^\d*\.?\d*$|^\.$/;
  return regex.test(input);
}

const CustomInput = React.memo(
  forwardRef(
    (
      {
        id,
        name,
        type = 'text',
        value,
        width,
        onChange,
        onFocus = () => {},
        placeholder,
        error,
        errorText = null,
        disabled,
        readOnly = false,
        rightIcon,
        multiline = false,
        rows,
        minRows,
        maxRows,
        rightIconOnClick,
        topLabel = '',
        inputWidth,
        isActive,
        withLabel,
        containerStyle,
        inputStyle,
        isDropdown = false,
        arrowIconIsActive = false,
        onBlur,
        autoComplete,
      },
      ref,
    ) => {
      const localInputRef = useRef(null);
      useImperativeHandle(ref, () => ({
        focus: () => {
          localInputRef.current.focus();
        },
        // other methods you want to provide...
      }));

      const handleChange = (e) => {
        if (type === 'text' || type === 'password') {
          return onChange(e);
        }
        if (type === 'number') {
          return validateNumberValue(e.target.value) && onChange(e);
        }
        return onChange(e);
      };

      const [isHovered, setIsHovered] = useState(false);
      const [isFocused, setIsFocused] = useState(false);
      const showClearButton = value && (isHovered || isFocused);

      return (
        <S.Container
          width={width}
          multiline={multiline}
          style={containerStyle}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {topLabel && <S.TopLabel>{topLabel}</S.TopLabel>}
          <S.StyledInputWrapper inputWidth={inputWidth} isActive={isActive} withLabel={withLabel}>
            <S.StyledInput
              id={id}
              name={name}
              type={type === 'password' ? type : undefined}
              value={value}
              width={width}
              onChange={handleChange}
              onFocus={() => {
                onFocus && onFocus();
                setIsFocused(true);
              }}
              placeholder={placeholder}
              variant='outlined'
              error={error}
              disabled={disabled}
              readOnly={readOnly}
              multiline={multiline}
              rows={rows}
              minRows={minRows}
              maxRows={maxRows}
              inputRef={localInputRef}
              onBlur={(e) => {
                onBlur && onBlur(e);
                setIsFocused(false);
              }}
              sx={inputStyle}
              autoComplete={autoComplete}
              righticon={rightIcon}
            />
            {(isDropdown || rightIcon) && (
              <S.IconsContainer isDropdown={isDropdown}>
                {isDropdown && (
                  <S.ArrowWrapper arrowIconIsActive={arrowIconIsActive}>
                    <ArrowDropDown />
                  </S.ArrowWrapper>
                )}
                {showClearButton && rightIcon && !disabled && (
                  <S.IconWrapper
                    onClick={(e) => {
                      if (!showClearButton) localInputRef.current.focus();
                      rightIconOnClick(e);
                    }}
                    isActive={isActive}
                  >
                    {rightIcon}
                  </S.IconWrapper>
                )}
              </S.IconsContainer>
            )}
            {disabled && (
              <S.DisabledIconWrapper>
                <Encrypt />
              </S.DisabledIconWrapper>
            )}
          </S.StyledInputWrapper>
          {error && errorText && (
            <S.ErrorWrapper>
              <ErrorMarker />
              <S.ErrorText>{errorText}</S.ErrorText>
            </S.ErrorWrapper>
          )}
        </S.Container>
      );
    },
  ),
);

CustomInput.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  type: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  width: PropTypes.number,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  placeholder: PropTypes.string,
  fullWidth: PropTypes.bool,
  error: PropTypes.bool,
  errorText: PropTypes.string,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  rightIcon: PropTypes.node,
  closeIcon: PropTypes.node,
  multiline: PropTypes.bool,
  rows: PropTypes.number,
  minRows: PropTypes.number,
  maxRows: PropTypes.number,
  rightIconOnClick: PropTypes.func,
  topLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  isActive: PropTypes.bool,
  isDropdown: PropTypes.bool,
  arrowIconIsActive: PropTypes.bool,
  arrowIconAction: PropTypes.func,
  autoComplete: PropTypes.oneOf(['off', '']),
};
CustomInput.displayName = 'CustomInput';

export default CustomInput;
